import { SCHEDULE_CHAIN, SERVICE_TYPE_BY_ID, addDays, calendarDateOf } from '../constants';
import { textoDaCelula } from './calculations/proximoPasso';
import { detalheDoEvento, resumoDoPeriodo, periodoPorExtenso } from './calendarioExportado';

/**
 * O RESUMO DE GESTÃO — o que vai para o dono do laboratório.
 *
 * Três seções e a mesma pergunta em cada uma: o que foi FEITO no período e o
 * que ficou EM ABERTO. Feito é histórico e vale para o período escolhido; em
 * aberto é a posição de HOJE, porque uma cobrança que venceu no período e foi
 * paga ontem não está mais em aberto, e um resumo que a listasse mandaria o
 * dono cobrar quem já pagou.
 *
 * O em aberto não é calculado aqui: vem de `pendenciasDoLaboratorio`, o mesmo
 * que acende o "Precisa de ação" do quadro. Uma terceira definição de
 * pendência discordaria das outras duas no primeiro caso de borda.
 *
 * Devolve só dados (seções com indicadores e tabelas); o HTML é do
 * relatorioPDF, que já sabe desenhar indicador e tabela.
 */

const reais = (v) => `R$ ${(Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dataBR = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—');
const dentro = (iso, de, ate) => !!iso && iso >= de && iso <= ate;
const soma = (linhas, campo) => linhas.reduce((s, l) => s + (Number(l[campo]) || 0), 0);
const plural = (q, um, varios) => `${q} ${q === 1 ? um : varios}`;

const lote = (s) => s.service_label || (s.service_code ? `#${s.service_code}` : '');

const etapasContratadas = (s) => (s.services_offered || [])
  .map(id => SERVICE_TYPE_BY_ID?.[id]?.sigla)
  .filter(Boolean)
  .join(', ');

/** Etapas concluídas no período, uma linha por etapa, na ordem do calendário. */
const etapasFeitas = (services, de, ate) => services
  .flatMap(s => SCHEDULE_CHAIN
    .filter(e => s[e.doneField])
    .map(e => ({ e, s, data: String(s[e.actualField] || s[e.field] || '').slice(0, 10) }))
    .filter(x => dentro(x.data, de, ate))
    .map(({ e, s, data }) => ({
      data,
      etapa: e.label,
      cliente: s.client_name,
      lote: lote(s),
      resultado: detalheDoEvento({ type: e.id, service: s })?.texto || '',
    })))
  .sort((a, b) => a.data.localeCompare(b.data) || a.etapa.localeCompare(b.etapa));

/** O que está agendado para os próximos sete dias a partir de hoje. */
const proximosDias = (services, hoje) => {
  const fim = addDays(hoje, 7);
  return services
    .flatMap(s => SCHEDULE_CHAIN
      .filter(e => !s[e.doneField] && (s.services_offered || []).includes(e.requires))
      .map(e => ({ e, s, data: calendarDateOf(s, e.id) }))
      .filter(x => x.data && x.data > hoje && x.data <= fim)
      .map(({ e, s, data }) => ({
        data,
        etapa: e.label,
        cliente: s.client_name,
        lote: lote(s),
        previsto: detalheDoEvento({ type: e.id, service: s })?.texto || '',
      })))
    .sort((a, b) => a.data.localeCompare(b.data));
};

/** Quem lançou o quê: pessoa × criou/alterou/excluiu. Gatilho sem autor sai. */
const atividadeDaEquipe = (trilha = []) => {
  const porPessoa = new Map();
  trilha.forEach(t => {
    if (!t.actor_name) return;
    if (!porPessoa.has(t.actor_name)) {
      porPessoa.set(t.actor_name, { pessoa: t.actor_name, criou: 0, alterou: 0, excluiu: 0 });
    }
    const l = porPessoa.get(t.actor_name);
    if (t.action === 'INSERT') l.criou += 1;
    else if (t.action === 'UPDATE') l.alterou += 1;
    else if (t.action === 'DELETE') l.excluiu += 1;
  });
  return [...porPessoa.values()]
    .map(l => ({ ...l, total: l.criou + l.alterou + l.excluiu }))
    .sort((a, b) => b.total - a.total);
};

const TIPO_DE_FATURA = { servico: 'Serviço', entrada: 'Entrada' };

/**
 * `dados`: o que `buscarGestao` trouxe. `pendencias`: o objeto de
 * `pendenciasDoLaboratorio`, calculado sobre TODOS os serviços. `hoje`: ISO.
 */
export const montarResumoDeGestao = ({
  de, ate, hoje, laboratorio, services = [], clients = [], dados = {}, pendencias = null,
  comDinheiro = false,
  // Uma linha por serviço com o que fazer — `linhasDeCobranca`, a mesma
  // situação que a tela do Financeiro desenha.
  cobranca = [],
}) => {
  const nomeDoCliente = (id) => clients.find(c => c.id === id)?.name || '—';
  const producao = resumoDoPeriodo(services, de, ate);
  const feitas = etapasFeitas(services, de, ate);
  const criados = services
    .filter(s => dentro(String(s.created_at || '').slice(0, 10), de, ate))
    .sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));
  const clientesNovos = clients.filter(c => dentro(String(c.created_at || '').slice(0, 10), de, ate));

  const faturas = dados.faturas || [];
  const recebimentos = dados.recebimentos || [];
  const abertas = dados.emAberto || [];
  const vencidas = abertas.filter(p => p.vencimento < hoje && Number(p.saldo) > 0);
  const aVencer = abertas.filter(p => p.vencimento >= hoje && p.vencimento <= addDays(hoje, 7) && Number(p.saldo) > 0);

  const lancamentos = pendencias?.lancamentos || [];

  const t = producao.total;
  const semen = dados.semen || [];
  const doses = (tipo) => semen.filter(m => m.tipo === tipo).reduce((s, m) => s + (Number(m.doses) || 0), 0);

  // ── Resumo ──────────────────────────────────────────────────────────────
  const resumo = {
    titulo: 'Resumo',
    indicadores: [
      { rotulo: 'Serviços criados', valor: String(criados.length),
        detalhe: clientesNovos.length ? plural(clientesNovos.length, 'cliente novo', 'clientes novos') : null },
      { rotulo: 'Etapas concluídas', valor: String(feitas.length) },
      comDinheiro && { rotulo: 'Faturado', valor: reais(soma(faturas, 'valor_total')),
        detalhe: plural(faturas.length, 'fatura', 'faturas') },
      comDinheiro && { rotulo: 'Recebido', valor: reais(soma(recebimentos, 'valor')),
        detalhe: plural(recebimentos.length, 'recebimento', 'recebimentos') },
      { rotulo: 'Em aberto hoje', valor: String(lancamentos.length + cobranca.length),
        detalhe: 'etapas e cobranças' },
    ].filter(Boolean),
    tabelas: [{
      titulo: 'Serviços criados',
      vazio: 'Nenhum serviço criado no período.',
      colunas: [
        { rotulo: 'Criado', valor: s => dataBR(String(s.created_at).slice(0, 10)) },
        { rotulo: 'Nº', valor: s => s.service_code || s.numero || '' },
        { rotulo: 'Cliente', valor: s => s.client_name, forte: true },
        { rotulo: 'Lote', valor: s => s.service_label || '' },
        { rotulo: 'Etapas', valor: etapasContratadas },
      ],
      linhas: criados,
    }],
  };

  // ── Produção ────────────────────────────────────────────────────────────
  const producaoSecao = {
    titulo: 'Produção',
    indicadores: [
      t.protocolos && { rotulo: 'Protocolo', valor: String(t.receptoras), detalhe: `receptoras em ${plural(t.protocolos, 'serviço', 'serviços')}` },
      t.opus && { rotulo: 'OPU', valor: String(t.oocitos), detalhe: `oócitos em ${plural(t.opus, 'OPU', 'OPUs')}` },
      t.fivs && { rotulo: 'FIV', valor: String(t.embrioes), detalhe: `embriões em ${plural(t.fivs, 'FIV', 'FIVs')}` },
      t.tes && { rotulo: 'TE', valor: String(t.transferidos), detalhe: `transferidos em ${plural(t.tes, 'TE', 'TEs')}` },
      t.dg30 && { rotulo: 'DG30', valor: producao.taxaDG30 || String(t.prenhes), detalhe: `${t.prenhes} prenhes de ${t.checadas}` },
      t.dg60 && { rotulo: 'DG60', valor: producao.taxaDG60 || String(t.confirmadas), detalhe: `${t.confirmadas} confirmadas de ${t.reconfirmar}` },
      (dados.congelados || []).length > 0 && { rotulo: 'Congelados', valor: String(dados.congelados.length), detalhe: 'embriões' },
      (dados.saidas || []).length > 0 && { rotulo: 'Saídas', valor: String(dados.saidas.length), detalhe: 'embriões' },
      (doses('entrada') || doses('saida')) && { rotulo: 'Sêmen', valor: `+${doses('entrada')} / −${doses('saida')}`, detalhe: 'doses entrada / saída' },
    ].filter(Boolean),
    tabelas: [
      {
        titulo: 'Feito — etapas concluídas',
        vazio: 'Nenhuma etapa concluída no período.',
        colunas: [
          { rotulo: 'Data', valor: l => dataBR(l.data) },
          { rotulo: 'Etapa', valor: l => l.etapa, forte: true },
          { rotulo: 'Cliente', valor: l => l.cliente },
          { rotulo: 'Lote', valor: l => l.lote },
          { rotulo: 'Resultado', valor: l => l.resultado },
        ],
        linhas: feitas,
      },
      {
        titulo: 'Em aberto — etapas sem lançamento',
        vazio: 'Nenhuma etapa sem lançamento.',
        colunas: [
          { rotulo: 'Etapa', valor: l => l.etapa, forte: true },
          { rotulo: 'Cliente', valor: l => l.service.client_name },
          { rotulo: 'Lote', valor: l => lote(l.service) },
          { rotulo: 'Agendada', valor: l => dataBR(l.agendada) },
          { rotulo: 'Parada há', valor: l => `${l.dias} dia${l.dias === 1 ? '' : 's'}`, numerica: true },
        ],
        linhas: lancamentos,
      },
      {
        titulo: 'Próximos 7 dias',
        vazio: 'Nada agendado.',
        colunas: [
          { rotulo: 'Data', valor: l => dataBR(l.data) },
          { rotulo: 'Etapa', valor: l => l.etapa, forte: true },
          { rotulo: 'Cliente', valor: l => l.cliente },
          { rotulo: 'Lote', valor: l => l.lote },
          { rotulo: 'Previsto', valor: l => l.previsto },
        ],
        linhas: proximosDias(services, hoje),
      },
      {
        titulo: 'Produção por cliente',
        colunas: [
          { rotulo: 'Cliente', valor: c => c.cliente, forte: true },
          { rotulo: 'Oócitos', valor: c => c.oocitos || '', numerica: true },
          { rotulo: 'Embriões', valor: c => c.embrioes || '', numerica: true },
          { rotulo: 'Transferidos', valor: c => c.transferidos || '', numerica: true },
          { rotulo: 'Prenhes DG30', valor: c => (c.dg30 ? `${c.prenhes} de ${c.checadas}` : ''), numerica: true },
        ],
        linhas: producao.clientes,
      },
    ],
  };

  // ── Financeiro ──────────────────────────────────────────────────────────
  const financeiro = comDinheiro && {
    titulo: 'Financeiro',
    indicadores: [
      { rotulo: 'Faturado', valor: reais(soma(faturas, 'valor_total')), detalhe: plural(faturas.length, 'fatura', 'faturas') },
      { rotulo: 'Recebido', valor: reais(soma(recebimentos, 'valor')), detalhe: plural(recebimentos.length, 'recebimento', 'recebimentos') },
      { rotulo: 'Vencido hoje', valor: reais(soma(vencidas, 'saldo')), detalhe: plural(vencidas.length, 'parcela', 'parcelas') },
      { rotulo: 'Vence em 7 dias', valor: reais(soma(aVencer, 'saldo')), detalhe: plural(aVencer.length, 'parcela', 'parcelas') },
      { rotulo: 'Total a receber', valor: reais(soma(abertas, 'saldo')), detalhe: plural(abertas.length, 'parcela', 'parcelas') },
    ],
    tabelas: [
      {
        titulo: 'Feito — faturas emitidas',
        vazio: 'Nenhuma fatura emitida no período.',
        colunas: [
          { rotulo: 'Emitida', valor: f => dataBR(f.emitida_em) },
          { rotulo: 'Fatura', valor: f => f.numero_exibicao || '' },
          { rotulo: 'Cliente', valor: f => nomeDoCliente(f.client_id), forte: true },
          { rotulo: 'Tipo', valor: f => TIPO_DE_FATURA[f.tipo] || f.tipo || '' },
          { rotulo: 'Valor', valor: f => reais(f.valor_total), numerica: true },
        ],
        linhas: [...faturas].sort((a, b) => String(a.emitida_em).localeCompare(String(b.emitida_em))),
      },
      /*
       * EM ABERTO, UMA LINHA POR SERVIÇO.
       *
       * Eram duas tabelas: parcelas vencidas, e "a cobrar ou faturar" listada
       * por MOTIVO. A mesma entrada atrasada saía três vezes — na tabela de
       * parcelas, como "Entrada vencida há 9d" e como "Parcela vencida há
       * 9d". Agora cada serviço aparece uma vez, com a entrada e o serviço
       * lado a lado e o que fazer, como na tela do Financeiro.
       */
      {
        titulo: 'Em aberto — o que cobrar e faturar',
        vazio: 'Nada a cobrar nem a faturar.',
        colunas: [
          { rotulo: 'Cliente', valor: ({ service }) => service.client_name || nomeDoCliente(service.client_id), forte: true },
          { rotulo: 'Serviço', valor: ({ service }) => [service.service_code ? `#${service.service_code}` : null, service.service_label].filter(Boolean).join(' · ') },
          { rotulo: 'Entrada', valor: ({ sit }) => textoDaCelula(sit.entrada) },
          { rotulo: 'Serviço', valor: ({ sit }) => textoDaCelula(sit.servico) },
          { rotulo: 'Próximo passo', valor: ({ sit }) => sit.passo?.rotulo || '' },
        ],
        linhas: cobranca,
      },
    ],
  };

  // ── Equipe ──────────────────────────────────────────────────────────────
  const equipe = atividadeDaEquipe(dados.trilha);
  const equipeSecao = equipe.length > 0 && {
    titulo: 'Equipe',
    nota: 'Registros criados, alterados e excluídos no sistema por pessoa.',
    indicadores: [],
    tabelas: [{
      titulo: 'Lançamentos no sistema',
      colunas: [
        { rotulo: 'Pessoa', valor: l => l.pessoa, forte: true },
        { rotulo: 'Criou', valor: l => l.criou || '', numerica: true },
        { rotulo: 'Alterou', valor: l => l.alterou || '', numerica: true },
        { rotulo: 'Excluiu', valor: l => l.excluiu || '', numerica: true },
        { rotulo: 'Total', valor: l => l.total, numerica: true },
      ],
      linhas: equipe,
    }],
  };

  return {
    titulo: `Resumo de gestão — ${periodoPorExtenso(de, ate)}`,
    laboratorio,
    filtros: [
      ['Período', `${dataBR(de)} a ${dataBR(ate)}`],
      ['Em aberto', `posição de ${dataBR(hoje)}`],
    ],
    secoes: [resumo, producaoSecao, financeiro, equipeSecao].filter(Boolean),
  };
};
